/**
 * First-class assumptions from a Launch Calendar / time-phased run (Slices 1–2).
 * Engine emits unvalidated assumptions; humans set confirmed | broken.
 * Engine may mark broken only when a fixed anchor is infeasible.
 */

import type { FundingAffordability } from './timePhasedFinancial.types';
import type {
  TimePhasedEngineInput,
  TimePhasedEngineOutput,
} from './timePhasedPlanning.types';
import { TIME_PHASED_ALGORITHM_VERSION } from './timePhasedPlanning.types';

/** Spec kinds — resource-allocation | date-hold | dependency-delivery | capacity | funding */
export type TimePhasedAssumptionKind =
  | 'resource-allocation'
  | 'date-hold'
  | 'dependency-delivery'
  | 'capacity'
  | 'funding';

/** Human-settable; engine never auto-confirms. */
export type TimePhasedAssumptionStatus = 'unvalidated' | 'confirmed' | 'broken';

export interface AssumptionQuantityWindow {
  quantity?: number;
  unit?: 'hours' | 'dollars' | 'weeks' | 'count';
  windowStart?: string;
  windowEnd?: string;
}

export interface AssumptionImpact {
  launchesAffected: Array<{
    priorityId: string;
    priorityName: string;
    effect: 'slips' | 'unlaunchable' | 'supply_exhausted' | 'blocked' | 'confidenceconfidence';
    slipWeeks?: number;
    oldDate?: string;
    newDate?: string;
  }>;
  teamsOverload: Array<{
    resourceType: string;
    bucketStart: string;
    utilizationPct: number;
    overageHours?: number;
  }>;
  fundingBreaks: Array<{
    bucketStart: string;
    overage: number;
  }>;
  /** One-line summary for the expandable row. */
  summary?: string;
  /** Relative magnitude for sort (higher = more load-bearing). */
  magnitude?: number;
}

export interface TimePhasedAssumption {
  id: string;
  kind: TimePhasedAssumptionKind;
  statement: string;
  /** Team / person / budget line / external party. */
  subject: string;
  quantity?: number;
  unit?: AssumptionQuantityWindow['unit'];
  windowStart?: string;
  windowEnd?: string;
  /** Placement / constraint ids that generated this assumption. */
  derived_from: string[];
  status: TimePhasedAssumptionStatus;
  /** True when status was set by a human (preserved across re-runs). */
  humanSet?: boolean;
  impactIfFalse?: string;
  impact?: AssumptionImpact;
  /** @deprecated use subject / quantity / window* */
  refs?: {
    priorityId?: string;
    priorityName?: string;
    resourceType?: string;
    windowStart?: string;
    windowEnd?: string;
    hours?: number;
  };
}

export interface TimePhasedAssumptionsSnapshot {
  ranAt: string;
  algorithmVersion: string;
  runKey?: string;
  assumptions: TimePhasedAssumption[];
}

function aid(kind: string, key: string): string {
  return `${kind}:${key}`;
}

function emptyImpact(): AssumptionImpact {
  return { launchesAffected: [], teamsOverload: [], fundingBreaks: [], magnitude: 0 };
}

export function impactMagnitude(impact?: AssumptionImpact | null): number {
  if (!impact) return 0;
  if (typeof impact.magnitude === 'number') return impact.magnitude;
  const slipScore = impact.launchesAffected.reduce(
    (s, l) => s + (l.slipWeeks ?? (l.effect === 'slips' ? 1 : 0)) * 10 + (l.effect === 'unlaunchable' || l.effect === 'supply_exhausted' || l.effect === 'blocked' ? 25 : 5),
    0,
  );
  const teamScore = impact.teamsOverload.reduce(
    (s, t) => s + Math.max(0, t.utilizationPct - 85) + (t.overageHours ?? 0),
    0,
  );
  const fundScore = impact.fundingBreaks.reduce((s, f) => s + Math.min(f.overage / 1000, 50), 0);
  return slipScore + teamScore + fundScore;
}

function summarizeImpact(impact: AssumptionImpact): string {
  const slips = impact.launchesAffected.filter(
    (l) => l.effect === 'slips' || (l.slipWeeks != null && l.slipWeeks > 0),
  );
  const worst = Math.max(0, ...slips.map((l) => l.slipWeeks ?? 0));
  const parts: string[] = [];
  if (slips.length > 0) {
    parts.push(
      `${slips.length} launch${slips.length === 1 ? '' : 'es'} slip` +
        (worst > 0 ? ` (worst: +${worst} weeks)` : ''),
    );
  } else if (impact.launchesAffected.length > 0) {
    parts.push(`${impact.launchesAffected.length} launch${impact.launchesAffected.length === 1 ? '' : 'es'} affected`);
  }
  if (impact.teamsOverload.length > 0) {
    const t = impact.teamsOverload[0];
    parts.push(`${t.resourceType} overloads`);
  }
  if (impact.fundingBreaks.length > 0) {
    const total = impact.fundingBreaks.reduce((s, f) => s + f.overage, 0);
    parts.push(`funding exceeds by $${Math.round(total).toLocaleString()}`);
  }
  return parts.length ? `If this breaks: ${parts.join(', ')}.` : 'If this breaks: schedule / capacity pressure rises.';
}

function launchesUsingTeam(
  output: TimePhasedEngineOutput,
  resourceType: string,
): AssumptionImpact['launchesAffected'] {
  return output.placements
    .filter(
      (p) =>
        p.impactedTeams?.includes(resourceType) ||
        (Array.isArray(p.rationale?.teams) &&
          (p.rationale.teams as string[]).includes(resourceType)),
    )
    .map((p) => ({
      priorityId: p.priorityId,
      priorityName: p.priorityName,
      effect:
        p.placementStatus === 'slipped'
          ? ('slips' as const)
          : p.placementStatus === 'supply_exhausted'
            ? ('supply_exhausted' as const)
            : !p.launchable
              ? ('unlaunchable' as const)
              : ('confidenceconfidence' as const),
      slipWeeks: p.slipWeeks,
      oldDate: p.targetLaunchStart,
      newDate: p.feasibleStart,
    }));
}

/** Preserve human-set confirmed/broken across engine re-derivation. */
export function mergePreservedAssumptionStatus(
  fresh: TimePhasedAssumptionsSnapshot,
  previous: TimePhasedAssumptionsSnapshot | null | undefined,
): TimePhasedAssumptionsSnapshot {
  if (!previous?.assumptions?.length) return fresh;
  const prevById = new Map(previous.assumptions.map((a) => [a.id, a]));
  return {
    ...fresh,
    assumptions: fresh.assumptions.map((a) => {
      const prev = prevById.get(a.id);
      if (prev?.humanSet && (prev.status === 'confirmed' || prev.status === 'broken')) {
        return { ...a, status: prev.status, humanSet: true };
      }
      // Engine may force broken on fixed-anchor infeasibility even if previously unvalidated
      if (a.status === 'broken' && !a.humanSet) return a;
      return a;
    }),
  };
}

/**
 * Pure derivation from placement output + financial + inputs.
 * Status defaults to unvalidated; engine sets broken only for fixed-anchor failure.
 */
export function deriveTimePhasedAssumptions(
  input: TimePhasedEngineInput,
  output: TimePhasedEngineOutput,
  financial?: { affordability: FundingAffordability | null } | null,
): TimePhasedAssumptionsSnapshot {
  const assumptions: TimePhasedAssumption[] = [];
  const byId = new Map(input.priorities.map((p) => [p.id, p]));

  const hotBuckets = (output.bucketUtilization ?? []).filter(
    (b) =>
      b.utilizationPct >= 85 ||
      b.demandHours > b.supplyHours ||
      b.demandHours > 0,
  );
  const byTeamWeek = new Map<string, (typeof hotBuckets)[0]>();
  for (const b of hotBuckets) {
    if (b.demandHours <= 0 && b.utilizationPct < 85) continue;
    const key = `${b.resourceType}::${b.bucketStart}`;
    const prev = byTeamWeek.get(key);
    if (!prev || b.utilizationPct > prev.utilizationPct) byTeamWeek.set(key, b);
  }

  const teamFirstWeek = new Map<string, (typeof hotBuckets)[0]>();
  for (const b of byTeamWeek.values()) {
    const cur = teamFirstWeek.get(b.resourceType);
    if (!cur || b.bucketStart < cur.bucketStart) teamFirstWeek.set(b.resourceType, b);
  }

  for (const [resourceType, first] of teamFirstWeek) {
    const teamBuckets = [...byTeamWeek.values()].filter((b) => b.resourceType === resourceType);
    const peak = teamBuckets.reduce((a, b) => (b.utilizationPct > a.utilizationPct ? b : a), first);
    const hours = Math.round(first.supplyHours);
    const launchesAffected = launchesUsingTeam(output, resourceType);
    const teamsOverload = teamBuckets
      .filter((b) => b.utilizationPct >= 85 || b.demandHours > b.supplyHours)
      .map((b) => ({
        resourceType: b.resourceType,
        bucketStart: b.bucketStart,
        utilizationPct: b.utilizationPct,
        overageHours: Math.max(0, b.demandHours - b.supplyHours),
      }));
    const impact: AssumptionImpact = {
      launchesAffected,
      teamsOverload,
      fundingBreaks: [],
    };
    impact.magnitude = impactMagnitude(impact);
    impact.summary = summarizeImpact(impact);

    assumptions.push({
      id: aid('resource-allocation', resourceType),
      kind: 'resource-allocation',
      subject: resourceType,
      quantity: hours,
      unit: 'hours',
      windowStart: first.bucketStart,
      windowEnd: peak.bucketEnd,
      derived_from: launchesAffected.map((l) => `placement:${l.priorityId}`),
      statement:
        `${resourceType} allocates ${hours}h starting week of ${first.bucketStart}` +
        (peak.utilizationPct >= 85
          ? ` (peak ${Math.round(peak.utilizationPct)}% — already tight)`
          : ''),
      status: 'unvalidated',
      impactIfFalse: impact.summary,
      impact,
      refs: {
        resourceType,
        windowStart: first.bucketStart,
        windowEnd: peak.bucketEnd,
        hours,
      },
    });

    // Also emit capacity kind when overloaded
    if (peak.utilizationPct >= 85) {
      assumptions.push({
        id: aid('capacity', resourceType),
        kind: 'capacity',
        subject: resourceType,
        quantity: hours,
        unit: 'hours',
        windowStart: first.bucketStart,
        windowEnd: peak.bucketEnd,
        derived_from: [`bucket:${resourceType}:${peak.bucketStart}`],
        statement: `${resourceType} capacity holds at ≤100% through week of ${peak.bucketStart}`,
        status: 'unvalidated',
        impactIfFalse: impact.summary,
        impact,
        refs: { resourceType, windowStart: first.bucketStart, hours },
      });
    }
  }

  const aff = financial?.affordability ?? null;
  if (aff && (aff.totalApproved > 0 || aff.totalForecastDemand > 0)) {
    const fundingBreaks = (aff.overloadedBuckets ?? []).map((b) => ({
      bucketStart: b.bucketStart,
      overage: b.overage,
    }));
    const launchesAffected = (aff.contributingPriorities ?? []).map((c) => ({
      priorityId: c.priorityId,
      priorityName: c.priorityName,
      effect: 'confidenceconfidence' as const,
    }));
    const impact: AssumptionImpact = { launchesAffected, teamsOverload: [], fundingBreaks };
    impact.magnitude = impactMagnitude(impact);
    impact.summary = summarizeImpact(impact);
    assumptions.push({
      id: aid('funding', 'scenario'),
      kind: 'funding',
      subject: 'Funding envelope',
      quantity: Math.round(aff.totalApproved || aff.totalForecastDemand),
      unit: 'dollars',
      derived_from: launchesAffected.map((l) => `placement:${l.priorityId}`),
      statement: aff.withinEnvelope
        ? `Funding envelope covers $${Math.round(aff.totalForecastDemand).toLocaleString()} forecast (of $${Math.round(aff.totalApproved).toLocaleString()} approved)`
        : `Funding envelope holds with $${Math.round(aff.overageTotal).toLocaleString()} overage pressure`,
      status: 'unvalidated',
      impactIfFalse: impact.summary,
      impact,
    });
  }

  for (const p of output.placements) {
    const pri = byId.get(p.priorityId);
    const anchoring = p.launchFlexibility ?? pri?.launchFlexibility ?? 'flexible';
    if (anchoring === 'fixed' && pri?.targetLaunchStart) {
      const broken =
        p.placementStatus === 'supply_exhausted' ||
        p.placementStatus === 'slipped' ||
        (p.slipWeeks != null && p.slipWeeks > 0);
      const impact: AssumptionImpact = {
        launchesAffected: [
          {
            priorityId: p.priorityId,
            priorityName: p.priorityName,
            effect:
              p.placementStatus === 'supply_exhausted'
                ? 'supply_exhausted'
                : p.placementStatus === 'slipped'
                  ? 'slips'
                  : 'confidenceconfidence',
            slipWeeks: p.slipWeeks,
            oldDate: pri.targetLaunchStart,
            newDate: p.feasibleStart,
          },
        ],
        teamsOverload: (p.impactedTeams ?? []).map((rt) => ({
          resourceType: rt,
          bucketStart: p.feasibleStart ?? pri.targetLaunchStart!,
          utilizationPct: 0,
        })),
        fundingBreaks: [],
      };
      impact.magnitude = impactMagnitude(impact) + (broken ? 40 : 10);
      impact.summary = broken
        ? `Cannot satisfy without moving “${p.priorityName}” (fixed anchor).`
        : summarizeImpact(impact);
      assumptions.push({
        id: aid('date-hold', p.priorityId),
        kind: 'date-hold',
        subject: p.priorityName,
        windowStart: pri.targetLaunchStart,
        windowEnd: pri.targetLaunchEnd,
        derived_from: [`placement:${p.priorityId}`, 'anchoring:fixed'],
        statement: `“${p.priorityName}” is fixed (hard) — launches week of ${pri.targetLaunchStart}` +
          (pri.targetLaunchEnd && pri.targetLaunchEnd !== pri.targetLaunchStart
            ? `–${pri.targetLaunchEnd}`
            : '') +
          (broken ? ' (cannot satisfy without moving this anchor)' : ''),
        // Engine may mark broken for fixed infeasibility; never auto-confirms.
        status: broken ? 'broken' : 'unvalidated',
        impactIfFalse: impact.summary,
        impact,
        refs: {
          priorityId: p.priorityId,
          priorityName: p.priorityName,
          windowStart: pri.targetLaunchStart,
          windowEnd: pri.targetLaunchEnd,
        },
      });
    } else if (anchoring === 'flexible' && (p.placementStatus === 'slipped' || (p.slipWeeks ?? 0) > 0)) {
      const impact: AssumptionImpact = {
        launchesAffected: [
          {
            priorityId: p.priorityId,
            priorityName: p.priorityName,
            effect: 'slips',
            slipWeeks: p.slipWeeks,
            oldDate: p.targetLaunchStart,
            newDate: p.feasibleStart,
          },
        ],
        teamsOverload: [],
        fundingBreaks: [],
      };
      impact.magnitude = impactMagnitude(impact);
      impact.summary = summarizeImpact(impact);
      assumptions.push({
        id: aid('date-hold', `flex:${p.priorityId}`),
        kind: 'date-hold',
        subject: p.priorityName,
        derived_from: [`placement:${p.priorityId}`, 'anchoring:flexible'],
        statement: `“${p.priorityName}” is flexible (soft) — may slide` +
          (p.slipWeeks ? ` (${p.slipWeeks}w from target)` : '') +
          (p.slackWeeks ? `; soft band ${p.slackWeeks}w` : ''),
        status: 'unvalidated',
        impactIfFalse: impact.summary,
        impact,
        refs: { priorityId: p.priorityId, priorityName: p.priorityName },
      });
    }
  }

  for (const p of output.placements.filter(
    (x) => x.placementStatus === 'blocked' || (!x.buildable && x.placementStatus !== 'below_line'),
  ).slice(0, 12)) {
    const impact: AssumptionImpact = {
      launchesAffected: [
        {
          priorityId: p.priorityId,
          priorityName: p.priorityName,
          effect: 'blocked',
        },
      ],
      teamsOverload: [],
      fundingBreaks: [],
    };
    impact.magnitude = impactMagnitude(impact) + 20;
    impact.summary = summarizeImpact(impact);
    assumptions.push({
      id: aid('dependency-delivery', p.priorityId),
      kind: 'dependency-delivery',
      subject: p.priorityName,
      derived_from: [`placement:${p.priorityId}`, 'dependency'],
      statement: `Predecessors / readiness for “${p.priorityName}” complete before its launch window`,
      status: 'unvalidated',
      impactIfFalse: impact.summary,
      impact,
      refs: { priorityId: p.priorityId, priorityName: p.priorityName },
    });
  }

  const committed = input.priorities.filter((p) => p.participationCommitted);
  const atRiskPart = input.priorities.filter((p) => p.participationAtRisk);
  if (committed.length > 0 || atRiskPart.length > 0) {
    const subjects = atRiskPart.length ? atRiskPart : committed;
    const impact: AssumptionImpact = {
      launchesAffected: subjects.slice(0, 12).map((p) => ({
        priorityId: p.id,
        priorityName: p.name,
        effect: 'unlaunchable' as const,
      })),
      teamsOverload: [],
      fundingBreaks: [],
    };
    impact.magnitude = impactMagnitude(impact);
    impact.summary = summarizeImpact(impact);
    assumptions.push({
      id: aid('resource-allocation', 'participation'),
      kind: 'resource-allocation',
      subject: 'Gate-team participation',
      derived_from: subjects.map((p) => `placement:${p.id}`),
      statement:
        atRiskPart.length > 0
          ? `${atRiskPart.length} launch${atRiskPart.length === 1 ? '' : 'es'} need gate-team participation reconfirmed`
          : `${committed.length} launch${committed.length === 1 ? '' : 'es'} assume gate-team participation stays committed`,
      status: 'unvalidated',
      impactIfFalse: impact.summary,
      impact,
    });
  }

  // Non-trivial run with zero assumptions is a bug — emit a sentinel so UI surfaces it.
  if (assumptions.length === 0 && output.placements.length > 0) {
    assumptions.push({
      id: aid('capacity', 'empty-run'),
      kind: 'capacity',
      subject: 'Scenario',
      derived_from: output.placements.map((p) => `placement:${p.priorityId}`),
      statement:
        'Run produced placements but no concrete capacity / date / dependency assumptions — check supply calendar and workload templates.',
      status: 'unvalidated',
      impactIfFalse: 'Assumption emission gap — treat calendar as uninspected.',
      impact: emptyImpact(),
    });
  }

  return {
    ranAt: new Date().toISOString(),
    algorithmVersion: output.algorithmVersion || TIME_PHASED_ALGORITHM_VERSION,
    assumptions,
  };
}

export function _countByKind(snapshot: TimePhasedAssumptionsSnapshot): Record<string, number> {
  const out: Record<string, number> = {};
  for (const a of snapshot.assumptions) {
    out[a.kind] = (out[a.kind] ?? 0) + 1;
  }
  return out;
}

export { emptyImpact, summarizeImpact };
